import * as React from "react";
import { ContentFlexComponent, ContentProps } from "components/flexComponents/Content/typings";
import {
  UitkCard,
  UitkCardContent,
  UitkCardLink,
  UitkCardMedia,
  UitkCardTextSegment,
  UitkCardImageLayout,
} from "uitk-react-cards";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { UitkScrim, UitkScrimType } from "uitk-react-scrim";
import { inject } from "mobx-react";
import { AspectRatioEnum, ThumborStore } from "stores/thumbor/ThumborStore";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { Action } from "components/utility/analytics/FlexAnalyticsUtils";
import { decorateEditorialHtml } from "components/flexComponents/Content/ContentUtilities";
import { ModuleViewType } from "components/flexComponents/Content/factory/EditorialContentFactory";
import { EditorialFlexModule } from "typings/microserviceModels/content-flex-module";
import {
  PageBackgroundContext,
  BackgroundColorTokens,
} from "src/components/flexComponents/PageLevelProviders/PageBackgroundColor/PageBackgroundContext.tsx";
import { LazyLoadedImage } from "src/components/utility/LazyLoadedImage/LazyLoadedImage";
import { SimpleContainerContext } from "src/components/flexComponents/SimpleContainer/SimpleContainer";
import { CTACarouselRegionContext } from "src/components/flexComponents/CTACarouselRegion/CTACarouselRegion";
import { ToggleContainerContext } from "src/components/flexComponents/ToggleContainer/ToggleContainer";
import { ResponsiveToggleContainerContext } from "src/components/flexComponents/ResponsiveToggleContainer/ResponsiveToggleContainer";

const CardMediaImage = inject("thumbor")(
  ({
    thumbor,
    flexComponent,
    context,
  }: {
    thumbor?: ThumborStore;
    flexComponent: ContentFlexComponent;
    context: ExtendedContextStore;
  }) => {
    if (!thumbor) {
      return null;
    }
    const model: EditorialFlexModule = flexComponent.model as EditorialFlexModule;
    const media = model.items[0].media || {};
    const { mediaAlt = "" } = media[0];

    return <LazyLoadedImage src={safelyResizeImage(flexComponent, context, thumbor)} alt={mediaAlt} />;
  }
);

export const safelyResizeImage = (
  flexComponent: ContentFlexComponent,
  context: ExtendedContextStore,
  thumbor: ThumborStore
): string => {
  const model: EditorialFlexModule = flexComponent.model as EditorialFlexModule;
  const isMobile = context && context.deviceInformation && context.deviceInformation.mobile;
  const quality = isMobile ? 80 : 100;

  return thumbor.safelyGenerateThumborizedImageUrl(
    model.items[0].media ? model.items[0].media[0].mediaUrl : "",
    1040,
    580,
    context,
    imageRatio(model.imageAspectRatio),
    true,
    quality
  );
};

export const imageRatio = (ratio: string) => {
  switch (ratio) {
    case "1-1": {
      return AspectRatioEnum.R_1_1;
    }
    case "3-2": {
      return AspectRatioEnum.R_3_2;
    }
    case "3-4": {
      return AspectRatioEnum.R_3_4;
    }
    case "4-3": {
      return AspectRatioEnum.R_4_3;
    }
    case "21-9": {
      return AspectRatioEnum.R_21_9;
    }
    default: {
      return AspectRatioEnum.DEFAULT;
    }
  }
};

export const uitkFigureAspectRatio = (ratio: string) => {
  switch (ratio) {
    case "1-1": {
      return UitkFigureAspectRatioType.R1_1;
    }
    case "3-2": {
      return UitkFigureAspectRatioType.R3_2;
    }
    case "3-4": {
      return UitkFigureAspectRatioType.R3_4;
    }
    case "4-3": {
      return UitkFigureAspectRatioType.R4_3;
    }
    case "21-9": {
      return UitkFigureAspectRatioType.R21_9;
    }
    default: {
      return UitkFigureAspectRatioType.R16_9;
    }
  }
};

const getCardScrimTypeByView = (view: string) => {
  switch (view) {
    case ModuleViewType.AffinityCard:
      return UitkScrimType.OVERLAY;
    default:
      return UitkScrimType.BOTTOM;
  }
};

const displayTextSegment = (view: string) => {
  switch (view) {
    case ModuleViewType.AffinityCard:
      return false;
    case ModuleViewType.ThemeSelectorCard:
      return false;
    default:
      return true;
  }
};

const getImageLayoutByView = (view: string) => {
  switch (view) {
    case ModuleViewType.Snippet:
      return UitkCardImageLayout.LEFT_FULLBLEED;
    default:
      return undefined;
  }
};

const getCardMediaWidthByView = (view: string) => {
  switch (view) {
    case ModuleViewType.Snippet:
      return 80;
    default:
      return undefined;
  }
};

const getCardClassByView = (view: string) => {
  switch (view) {
    case ModuleViewType.Snippet:
      return "snippet";
    default:
      return "cardWithLink";
  }
};

const getFigureAspectRatioByView = (view: string, aspectRatio: string) => {
  switch (view) {
    case ModuleViewType.Snippet:
      return undefined;
    default:
      return uitkFigureAspectRatio(aspectRatio);
  }
};

const getHeaderClassByView = (view: string) => {
  switch (view) {
    case ModuleViewType.ThemeSelectorCard:
      return "uitk-type-regular cardEditorialHeading uitk-type-heading-500 uitk-type-left";
    default:
      return "cardEditorialHeading uitk-type-heading-500 uitk-type-left";
  }
};

export const EditorialWithLink = (props: ContentProps) => {
  const model: EditorialFlexModule = props.flexComponent.model as EditorialFlexModule;
  const moduleName = props.flexComponent.name;
  const cardLabel = model.items[0].title ? model.items[0].title : model.title;
  const cardBody = model.items[0].text ?? "";
  const cardSubLabel = model.items[0].subtitle ? model.items[0].subtitle : model.subtitle;
  const cardName = cardLabel && cardLabel.replace(" ", "_");
  const rfrr = `${moduleName}.${cardName}.${Action.CLICK}`;
  const cardClassName = getHeaderClassByView(model.view);
  const dynamicClickUrl = model.items[0].media ? model.items[0].media[0].clickUrl : "";
  const url = dynamicClickUrl && dynamicClickUrl.length > 0 ? dynamicClickUrl : model.cardLinkUrl;
  const excludeRfrr = model.excludeRfrr;
  const cardLink = () => {
    return excludeRfrr ? `${url}` : `${url}?rfrr=${rfrr}`;
  };
  const { flexComponent, context } = props;

  const editorialViewClass = getCardClassByView(model.view);

  const backgroundColor = React.useContext(PageBackgroundContext);
  const withBorder = model.boxType !== "no-border" && backgroundColor === BackgroundColorTokens.BACKGROUND_WHITE;

  const simpleContainerContext = React.useContext(SimpleContainerContext);
  const toggleContainerContext = React.useContext(ToggleContainerContext);
  const responsiveToggleContainerContext = React.useContext(ResponsiveToggleContainerContext);
  const ctaCarouselContext = React.useContext(CTACarouselRegionContext);
  const shouldRenderBorder =
    withBorder ||
    simpleContainerContext.modulesHaveBorder ||
    toggleContainerContext.modulesHaveBorder ||
    responsiveToggleContainerContext.modulesHaveBorder ||
    ctaCarouselContext.hasBorder;

  /**
   * This is a temporary work around due to limitations of the editorial model that will be addressed in the future.
   * Parsing data from contributed HTML content should not be considered an accepted approach to be repeated elsewhere
   */

  const [, title] = /<h3 [^>]+>(.*?)<\/h3>/g.exec(cardBody) || [];

  const [, titleSpan] = /<span [^>]+>(.*?)<\/span>/g.exec(title) || [];

  const a11yText = titleSpan ?? title;

  return (
    <UitkCard
      imageLayout={getImageLayoutByView(model.view)}
      className={editorialViewClass}
      withBorder={shouldRenderBorder}
    >
      <UitkCardMedia pxWidth={getCardMediaWidthByView(model.view)} className={editorialViewClass}>
        <UitkFigure ratio={getFigureAspectRatioByView(model.view, model.imageAspectRatio)}>
          <CardMediaImage flexComponent={flexComponent} context={context} />
          <UitkScrim type={getCardScrimTypeByView(model.view)}>
            {cardLabel && <h3 className={cardClassName}>{cardLabel}</h3>}
            {cardSubLabel && <div className="uitk-type-left uitk-type-subheading">{cardSubLabel}</div>}
          </UitkScrim>
        </UitkFigure>
      </UitkCardMedia>
      <UitkCardContent>
        {displayTextSegment(model.view) && model.items[0].text && (
          <UitkCardTextSegment className="uitk-type-paragraph-200">
            <div className="uitk-type-left" dangerouslySetInnerHTML={decorateEditorialHtml(cardBody)} />
          </UitkCardTextSegment>
        )}
        <UitkCardLink>
          <a href={cardLink()} data-testid="editorial-with-link-anchor">
            {a11yText || model.title || moduleName}
          </a>
        </UitkCardLink>
      </UitkCardContent>
    </UitkCard>
  );
};

export default EditorialWithLink;
